import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import Paper from '@material-ui/core/Paper';

import { JobContext } from 'context/jobContextContainer';
import { driversService } from 'services/driversService';
import { buildVehicleLabelMap, getVehiclesFromIdsResponseData } from 'utils/driverVehicleLabel';
import {
  getRouteViewDriverSummaryLabel,
  shouldShowRouteViewDriverSummary,
} from 'utils/routeViewDriver';
import { PergoTitle } from '../PergoTitle';

const DriverRoutesSection = () => {
  const { selectedRouteDriverId, setSelectedRouteDriverId, routeViewDriverSummaries, driverList } =
    useContext(JobContext);
  const [vehicleLabelById, setVehicleLabelById] = useState({});

  useEffect(() => {
    const vehicleIds = [
      ...new Set(
        routeViewDriverSummaries
          .map(summary => parseInt(summary.vehicle_id, 10))
          .filter(id => Number.isFinite(id) && id > 0)
      ),
    ];

    if (!vehicleIds.length) {
      setVehicleLabelById({});
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const response = await driversService.get_vehicles_from_ids(vehicleIds);
        if (cancelled) {
          return;
        }
        setVehicleLabelById(buildVehicleLabelMap(getVehiclesFromIdsResponseData(response)));
      } catch {
        if (!cancelled) {
          setVehicleLabelById({});
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [routeViewDriverSummaries]);

  const clearRouteSelection = () => {
    setSelectedRouteDriverId(null);
  };

  const handleDriverClick = driverId => {
    const id = parseInt(driverId, 10);
    if (!Number.isFinite(id) || id < 0) {
      return;
    }
    if (selectedRouteDriverId === id) {
      clearRouteSelection();
      return;
    }
    setSelectedRouteDriverId(id);
  };

  const getDriverLabel = summary => getRouteViewDriverSummaryLabel(summary, driverList);

  const getVehicleLabel = summary => {
    const vehicleId = parseInt(summary.vehicle_id, 10);
    if (Number.isFinite(vehicleId) && vehicleId > 0 && vehicleLabelById[vehicleId]) {
      return vehicleLabelById[vehicleId];
    }
    return '';
  };

  const driverRouteSummaries = routeViewDriverSummaries.filter(summary =>
    shouldShowRouteViewDriverSummary(summary, driverList)
  );

  return (
    <DriverRoutesSectionWrapper className="main--driver-routes-section-wrapper">
      <Paper className="main--driver-routes-section" elevation={3} square>
        <PergoTitle className="main--driver-routes-title">Driver Routes</PergoTitle>
        <div className="main--driver-routes-body">
          <p className="driver-routes-helper">
            Click a driver to show their route on the map and the trip sequence in the table, including travel
            between each dropoff and the next pickup. Click the same driver again to clear the selection and return
            to the full jobs list.
          </p>
          <div className="driver-routes-list">
            {driverRouteSummaries.length === 0 ? (
              <p className="driver-routes-empty">No driver routes for the selected date.</p>
            ) : (
              driverRouteSummaries.map(summary => {
                const isActive = selectedRouteDriverId === summary.driver_id;
                const label = getDriverLabel(summary);
                const driverNo = label.split(' - ')[0] || '';
                const driverName = label.includes(' - ')
                  ? label.split(' - ').slice(1).join(' - ')
                  : label;

                return (
                  <button
                    key={summary.driver_id}
                    type="button"
                    className={`driver-route-item${isActive ? ' active' : ''}`}
                    onClick={() => handleDriverClick(summary.driver_id)}
                  >
                    <div className="driver-route-item-head">
                      <span className="driver-route-number">{driverNo}</span>
                      <span className="driver-route-name">{driverName}</span>
                    </div>
                    <div className="driver-route-item-meta">
                      <span>{getVehicleLabel(summary)}</span>
                      <span>Job Count : {summary.job_count}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </Paper>
    </DriverRoutesSectionWrapper>
  );
};

export default DriverRoutesSection;

const DriverRoutesSectionWrapper = styled.div`
  flex: 1;
  max-width: 500px;
  min-width: 445px;
  margin-right: 10px;
  height: 590px;
  max-height: calc(100vh - 200px);
  display: flex;
  flex-direction: column;
  align-self: flex-start;

  .main--driver-routes-section {
    border: 2px solid var(--blue-main-color);
    height: 100%;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: #f5f7f8;

    .main--driver-routes-title {
      flex-shrink: 0;
      background: #0f7aad;
      font-size: 16px;
      padding: 15px 10px;
    }

    .main--driver-routes-body {
      flex: 1;
      min-height: 0;
      padding: 10px;
      overflow-y: auto;
      overflow-x: hidden;
    }

    .driver-routes-helper {
      margin: 0 0 12px;
      color: #5c6b73;
      font-size: 13px;
      line-height: 1.45;
    }

    .driver-routes-empty {
      color: #67757d;
      font-size: 13px;
    }

    .driver-routes-list {
      display: grid;
      gap: 8px;
    }

    .driver-route-item {
      padding: 12px;
      border: 1px solid #c9d3d8;
      background: #ffffff;
      color: inherit;
      text-align: left;
      cursor: pointer;
      width: 100%;

      &.active {
        border-color: #1483b8;
        background: #e9f6fd;
      }
    }

    .driver-route-item-head {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .driver-route-number {
      display: inline-block;
      min-width: 34px;
      padding: 5px 6px;
      background: #0c4765;
      color: #ffffff;
      font-size: 12px;
      font-weight: 700;
      text-align: center;
    }

    .driver-route-name {
      font-size: 18px;
      font-weight: 700;
    }

    .driver-route-item-meta {
      display: flex;
      justify-content: space-between;
      margin-top: 10px;
      color: #67757d;
      font-size: 12px;
      gap: 10px;
    }
  }

  @media (max-width: 830px) {
    max-width: 100%;
    min-width: 0;
    margin-right: 0;
    margin-bottom: 10px;
    height: 400px;
    max-height: none;
  }
`;
